import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../../shared/types';
import { SupportTicketModel } from '../../../administrator/support/models/SupportTicket.model';
import getCommunitySupportTicket from './operations/GetCommunitySupportTicket';
import getCommunitySupportTickets from './operations/GetCommunitySupportTickets';

export interface CommunitySupportState {
  supportTickets: SupportTicketModel[];
  ticketPagination: PaginationMetaModel;
  supportTicket: SupportTicketModel | null;
  getCommunitySupportTicketsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCommunitySupportTicketStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: CommunitySupportState = {
  supportTickets: [],
  supportTicket: null,
  ticketPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  getCommunitySupportTicketsStatus: {
    status: 'idle',
    error: null,
  },
  getCommunitySupportTicketStatus: {
    status: 'idle',
    error: null,
  },
};

const communitySupportSlice = createSlice({
  name: 'communitySupportSlice',
  initialState,
  reducers: {
    setSelectedTicket: (
      state,
      action: PayloadAction<SupportTicketModel | null>,
    ) => {
      state.supportTicket = action.payload;
    },
    clearSelectedTicket: (state) => {
      state.supportTicket = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCommunitySupportTickets.pending, (state) => {
      state.getCommunitySupportTicketsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getCommunitySupportTickets.fulfilled,
      (state, { payload }) => {
        state.getCommunitySupportTicketsStatus = {
          status: 'success',
          error: null,
        };
        state.supportTickets = payload.model;
        state.ticketPagination = payload.meta;
      },
    );
    builder.addCase(
      getCommunitySupportTickets.rejected,
      (state, { payload }) => {
        state.getCommunitySupportTicketsStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to fetch community support tickets',
        };
      },
    );
    builder.addCase(getCommunitySupportTicket.pending, (state) => {
      state.getCommunitySupportTicketStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getCommunitySupportTicket.fulfilled,
      (state, { payload }) => {
        state.getCommunitySupportTicketStatus = {
          status: 'success',
          error: null,
        };
        state.supportTicket = payload;
      },
    );
    builder.addCase(
      getCommunitySupportTicket.rejected,
      (state, { payload }) => {
        state.getCommunitySupportTicketStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to fetch support ticket',
        };
      },
    );
    builder.addCase(resetAll, () => initialState);
  },
});

export const {
  setSelectedTicket,
  clearSelectedTicket,
} = communitySupportSlice.actions;

export default communitySupportSlice.reducer;
